/**
 * O parser de transcrição: transformar o arquivo de texto em falas.
 *
 * <p>Mora fora do componente porque é a única parte desta tela que pode estar errada
 * sem parecer errada — um heurístico de "quem falou" acerta na amostra e erra no
 * arquivo de verdade. Fora do React, ele tem teste.
 */

/**
 * Uma fala reconhecida no texto.
 *
 * <p>{@code quem} nulo é texto que não é fala de ninguém — cabeçalho do arquivo, nota
 * do transcritor, linha solta. Ele continua aparecendo: adivinhar errado e esconder
 * seria pior que mostrar sem enfeite.
 */
export interface Fala {
  quem: string | null;
  quando: string | null;
  texto: string;
}

/**
 * O que conta como início de fala.
 *
 * <p>Casa os formatos que as ferramentas de transcrição produzem — "Theo:",
 * "[00:12:30] Theo:", "00:12 Theo:" — e para no primeiro dois-pontos. O limite de 40
 * caracteres no nome é o que impede uma frase inteira que por acaso tem dois-pontos no
 * meio de virar "orador".
 */
const FALA = /^(?:[[(]?(\d{1,2}:\d{2}(?::\d{2})?)[\])]?\s*)?([^:\n]{1,40}?):\s*(.*)$/;

/**
 * Um nome que aparece uma vez só ainda parece nome?
 *
 * <p>Duas pistas, e as duas precisam valer: <b>começa com maiúscula</b> e tem <b>no
 * máximo duas palavras</b>. Rótulo de orador é nome próprio — "Hiro", "André Aponte";
 * anotação é prosa — "nota do transcritor", "obs", "áudio cortado aqui".
 *
 * <p>Existe porque a primeira régua (repetição ou carimbo) errava para o lado errado:
 * quem falasse uma vez só na reunião inteira perdia o nome e virava anotação. Perder o
 * enfeite de uma nota é feio; perder a atribuição de um participante é perder conteúdo.
 */
function pareceNome(nome: string): boolean {
  return /^[A-ZÀ-Ý]/.test(nome) && nome.split(/\s+/).length <= 2;
}

/**
 * Quem realmente fala nesta transcrição.
 *
 * <p>A regex sozinha aceita demais: "nota do transcritor: áudio cortado" tem a mesma
 * forma de uma fala, e virava um orador com chip e cor. A evidência que separa os dois
 * é, em ordem de força: <b>repetição</b> (quem participa fala mais de uma vez),
 * <b>carimbo de tempo</b> (nenhuma ferramenta carimba anotação), e — para quem falou
 * uma vez só — <b>a forma do nome</b>.
 */
export function oradoresDe(linhas: string[]): Set<string> {
  const quantas = new Map<string, number>();
  const comHorario = new Set<string>();

  for (const linha of linhas) {
    const achou = FALA.exec(linha);
    if (!achou) continue;
    const nome = achou[2].trim();
    quantas.set(nome, (quantas.get(nome) ?? 0) + 1);
    if (achou[1]) comHorario.add(nome);
  }

  return new Set(
    [...quantas.entries()]
      .filter(([nome, vezes]) => vezes > 1 || comHorario.has(nome) || pareceNome(nome))
      .map(([nome]) => nome)
  );
}

export function separarEmFalas(texto: string): Fala[] {
  const linhas = texto.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const oradores = oradoresDe(linhas);
  const falas: Fala[] = [];

  for (const linha of linhas) {
    const achou = FALA.exec(linha);
    if (achou) {
      const nome = achou[2].trim();
      // Tem forma de fala mas o nome não passou no teste de orador: é anotação. Fica
      // como linha solta em vez de grudar na fala de cima — "nota do transcritor:
      // áudio cortado" colado no fim da frase do Theo o faria dizer o que não disse.
      if (oradores.has(nome)) {
        falas.push({ quem: nome, quando: achou[1] ?? null, texto: achou[3].trim() });
      } else {
        falas.push({ quem: null, quando: null, texto: linha });
      }
      continue;
    }
    // Continuação da fala anterior: uma fala longa quebrada em várias linhas não deve
    // virar vários blocos com o mesmo nome repetido.
    const anterior = falas.at(-1);
    if (anterior && anterior.quem) anterior.texto += (anterior.texto ? ' ' : '') + linha;
    else falas.push({ quem: null, quando: null, texto: linha });
  }
  return falas;
}

